import { PreviewsService as PreviewsApi } from '../../shared/api/generated/previews/previews.service';
import { ScrapeUrlResponseSchema } from '@andor/contracts/previews';
// Resolves the OGP preview card for a message that contains a URL.
//
// Extracted from `ChatService.buildMessage` so the OGP fetch — a transient
// best-effort thing — isn't entangled with the main message-build path
// (which is doing routing, sounds, notifications, markdown formatting,
// and store mutation). The fixture service also calls in here, which means
// "where does OGP run?" has one answer instead of two.
//
// Behaviors worth knowing:
//   - **Per-URL session cache** keyed on the URL. Storing the Promise (not
//     just the resolved value) means parallel `resolveForMessage` calls for
//     the same URL share a single fetch — no double round-trip when 30
//     messages all link the same page (or when the stress fixture pours
//     50 instances of the same URL into the store at once).
//   - **Timeout**: requests give up after `_TIMEOUT_MS` to keep a stuck
//     OGP backend from leaving promises pending forever.
//   - **Silent failure** — null on network error, timeout, backend error,
//     or "OGP returned no usable title". The embed is a progressive
//     enhancement; the message renders fine without it.
//   - **Prune-race guard** — re-checks `MessageStore.has(id)` after the
//     async fetch before upserting. Without this, an OGP that arrives
//     after the per-room cap evicted its message would resurrect it.
//   - **URL source**: `payload.text` holds the raw markdown source on
//     every code path (receive, fixture, send), so URL detection runs
//     against unrendered text — no need to peek through `<a>` tags.
import { inject, Injectable } from '@angular/core';
import { firstValueFrom, timeout } from 'rxjs';
import { Message, MessageStore } from '../../state/messages.repository';
import { extractFirstUrl } from '../../shared/helpers/url-detector';
import { imageDimensions } from '../../shared/helpers/image-dimensions';

interface ResolvedEmbed {
  title: string;
  image?: string;
  imageWidth?: number;
  imageHeight?: number;
  description?: string;
}

// Pages without `og:title` / `<title>` legitimately return only a
// description (and/or image) from `/v1/ogs`. Falling back to the
// hostname keeps the link card useful as a clickable label instead
// of suppressing the preview entirely.
function hostnameOf(url: string): string | null {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
}

@Injectable({ providedIn: 'root' })
export class EmbedResolverService {
  private readonly previewsApi = inject(PreviewsApi);

  private static readonly _TIMEOUT_MS = 5000;

  private _cache = new Map<string, Promise<ResolvedEmbed | null>>();

  constructor(private _messageStore: MessageStore) {}

  // Cached, deduped, timeout-bounded OGP lookup. Same URL = same promise.
  resolve(url: string): Promise<ResolvedEmbed | null> {
    let pending = this._cache.get(url);
    if (!pending) {
      pending = this._fetch(url);
      this._cache.set(url, pending);
    }
    return pending;
  }

  // Picks the first URL out of the message's text, resolves it, upserts
  // the embed onto the stored message. No-op when there is no URL, no
  // text, or the message has been evicted from the store during the fetch.
  async resolveForMessage(message: Message): Promise<void> {
    if (message.kind !== 'message') return;
    const text = message.payload?.text;
    if (!text) return;
    const url = extractFirstUrl(text);
    if (!url) return;
    const embed = await this.resolve(url);
    if (!embed) return;
    if (!this._messageStore.has(message.id)) return;
    message.payload.embedUrl = { title: embed.title, href: url };
    if (embed.image) message.payload.embedUrl.image = embed.image;
    if (embed.imageWidth) message.payload.embedUrl.imageWidth = embed.imageWidth;
    if (embed.imageHeight) message.payload.embedUrl.imageHeight = embed.imageHeight;
    if (embed.description) message.payload.embedUrl.description = embed.description;
    this._messageStore.upsert(message);
  }

  private async _fetch(url: string): Promise<ResolvedEmbed | null> {
    try {
      const raw = ScrapeUrlResponseSchema.parse(
        await firstValueFrom(
          this.previewsApi
            .scrapeUrl({ url, downloadImage: true })
            .pipe(timeout(EmbedResolverService._TIMEOUT_MS))
        )
      );
      if (!raw?.title && !raw?.description && !raw?.imageUrl) return null;
      const title = raw.title ?? hostnameOf(url);
      if (!title) return null;
      const embed: ResolvedEmbed = { title, description: raw.description };
      if (raw.imageUrl) {
        embed.image = raw.imageUrl;
        // Decode the image once to read its intrinsic size. Failure is
        // non-fatal — the embed still lands without dims and the row
        // reflows on first paint (legacy behavior, just for that embed).
        try {
          const dims = await imageDimensions(raw.imageUrl);
          embed.imageWidth = dims.width;
          embed.imageHeight = dims.height;
        } catch {
          // No dims — see note above.
        }
      }
      return embed;
    } catch {
      return null;
    }
  }
}
